import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const ProjetoDesenhoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_proj_des: '',
    id_projeto: '',
    id_desenho: '',
    data_associacao: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [projetoSearch, setProjetoSearch] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar projetos com base na busca
  const projetosFiltrados = useMemo(() => {
    if (!projetoSearch || projetoSearch.trim() === '') return projetos;
    const searchTerm = projetoSearch.toLowerCase().trim();
    return projetos.filter(proj =>
      proj.id_projeto?.toLowerCase().includes(searchTerm) ||
      proj.nome_escola?.toLowerCase().includes(searchTerm) ||
      proj.descr_tp_projeto?.toLowerCase().includes(searchTerm)
    );
  }, [projetos, projetoSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'desenho do projeto', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/projeto-desenho/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'desenho do projeto', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'desenho do projeto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'desenho do projeto', { id });
    try {
      const response = await api.delete(`/projeto-desenho/${id}`);
      logarSucesso('Excluir', 'desenho do projeto', response.data);
      setDados(currentData => currentData.filter(item => item.id_proj_des !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'desenho do projeto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const colunasBase = [
      {
        title: 'Id projeto desenho',
        field: 'id_proj_des',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Projeto',
        field: 'id_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Projeto...',
        sorter: 'string',
      },
      {
        title: 'Desenho',
        field: 'descricao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Desenho...',
        sorter: 'string',
      },
      {
        title: 'Data Associação',
        field: 'data_associacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Data...',
        sorter: 'date',
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_proj_des, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_proj_des}"?`)) {
            excluirRegistro(data.id_proj_des);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => {
        if (col.field === 'id_proj_des' || col.field === 'id_projeto' || col.field === 'descricao') {
          return { ...col, editor: undefined };
        }
        return { ...col, editor: col.editor || 'input' };
      })
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_proj_des'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/projeto-desenho');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/projeto-desenho');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      if (error.response && error.response.status === 403) {
        mostrarMensagem('error', 'Você não tem permissão para acessar este recurso.');
      } else {
        mostrarMensagem('error', `Erro de conexão: ${error.message}`);
      }
    }
  }, [mostrarMensagem]);

  const fetchProjetos = useCallback(async () => {
    try {
      const response = await api.get('/projeto');
      setProjetos(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar projetos: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchProjetos();
  }, [verificarAPI, fetchProjetos]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!formData.id_proj_des.trim()) {
      camposObrigatorios.push('ID Proj Desenho');
      erros.push('id_proj_des');
    }
    if (!formData.id_projeto.trim()) {
      camposObrigatorios.push('Projeto');
      erros.push('id_projeto');
    }
    if (!formData.id_desenho.trim()) {
      camposObrigatorios.push('Desenho');
      erros.push('id_desenho');
    }
    if (!formData.data_associacao.trim()) {
      camposObrigatorios.push('Data Associação');
      erros.push('data_associacao');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    logarRequisicao('Adicionar', 'desenho do projeto', formData);
    try {
      setLoading(true);
      const response = await api.post('/projeto-desenho', formData);
      logarSucesso('Adicionar', 'desenho do projeto', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_proj_des: '', id_projeto: '', id_desenho: '', data_associacao: '' });
      setProjetoSearch('');
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'desenho do projeto');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Remove o erro do campo quando ele for preenchido
    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'projeto_desenho.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Projeto-Desenho</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela de projeto-desenho</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/projeto desenho.mov" buttonText="Ver Tutorial Projeto Desenho" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id projeto desenho">ID projeto desenho:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_proj_des"
              name="id_proj_des"
              value={formData.id_proj_des}
              onChange={handleInputChange}
              placeholder="Ex: PROJ-DES-001"
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_proj_des') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="id_projeto">Projeto:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_projeto"
              name="id_projeto"
              value={projetoSearch}
              onChange={(e) => {
                setProjetoSearch(e.target.value);
                const projetoEncontrado = projetos.find(proj =>
                  proj.id_projeto === e.target.value ||
                  `${proj.id_projeto} - ${proj.nome_escola || ''}`.trim() === e.target.value
                );
                if (projetoEncontrado) {
                  setFormData(prev => ({ ...prev, id_projeto: projetoEncontrado.id_projeto }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_projeto'));
                } else {
                  setFormData(prev => ({ ...prev, id_projeto: '' }));
                }
              }}
              list="projetos-list-desenho"
              placeholder="Digite para buscar projeto..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_projeto') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="projetos-list-desenho">
              {projetosFiltrados.map(item => (
                <option key={item.id_projeto} value={item.id_projeto}>
                  {item.nome_escola ? `${item.nome_escola} - ${item.descr_tp_projeto || ''}` : item.id_projeto}
                </option>
              ))}
            </datalist>
          </div>

          <div className="form-group">
            <label htmlFor="id_desenho">Desenho:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_desenho"
              name="id_desenho"
              value={formData.id_desenho}
              onChange={handleInputChange}
              placeholder="Ex: DES-001"
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_desenho') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="data_associacao">Data associação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="date"
              id="data_associacao"
              name="data_associacao"
              value={formData.data_associacao}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('data_associacao') ? 'input-error' : ''}
            />
          </div>

          <div className="actions-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap' }}>
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default ProjetoDesenhoManager;